import { Fragment, useEffect, useState } from 'react'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useInfiniteQuery } from '@tanstack/react-query'
import clsx from 'clsx'

import Layout from '@/templates/Layout/Layout'
import Link from '@/atoms/Link/Link'
import Button from '@/atoms/Button/Button'
import FilterChipButton from '@/atoms/Button/FilterChipButton'
import SamplesFilterDropdown from '@/molecules/SamplesFilterDropdown/SamplesFilterDropdown'
import SamplesListing from '@/templates/SamplesListing/SamplesListing'
import GettingStarted from '@/templates/GettingStarted/GettingStarted'

/* assets */
import { ReactComponent as LoaderIcon } from 'public/assets/icons/loader.svg'

/* utils */
import { getWordpressGraphqlData } from '@/utils/api/wrapper'
import { getContentCategoryMetadata } from '@/utils/functions/samples'
import {
  contentCategoryPageQuery,
  sampleListQuery
} from '@/utils/queries/samples_list'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'
import { constructBreadcrumbJsonSchema } from '@/utils/functions/jsonSchema'
import { triggerServiceOrderingFlow } from '@/utils/functions/order'

interface Props {
  postsData: SamplePostListingResponse
  initialFilterState: FilterStateType
  contentTypes: { id: string; name: string; slug: string }[]
  industries: { id: string; name: string; slug: string }[]
  creatorLevels: { id: string; name: string; slug: string }[]
  wpContentCategorySlug: WpContentCategorySlug
  categorySlug: string
}

export type FilterStateType = {
  contentType: string
  creatorLevel: string
  industry: string
}

const categoryList = [
  {
    title: getContentCategoryMetadata('text').title,
    slug: getContentCategoryMetadata('text').slug
  },
  {
    title: getContentCategoryMetadata('design').title,
    slug: getContentCategoryMetadata('design').slug
  },
  {
    title: getContentCategoryMetadata('vernacular').title,
    slug: getContentCategoryMetadata('vernacular').slug
  }
]

const initialFilterState = {
  contentType: '',
  creatorLevel: '',
  industry: ''
}

export default function CategorySamplesPage({
  postsData,
  categorySlug,
  wpContentCategorySlug,
  contentTypes,
  industries,
  creatorLevels,
  initialFilterState
}: Props) {
  const router = useRouter()

  const [filters, setFilters] = useState<FilterStateType>({
    ...initialFilterState
  })

  // * * remove filter on category change
  useUpdateEffect(() => {
    setFilters({ ...initialFilterState })
  }, [categorySlug])

  // * * set filter from query params
  useEffect(() => {
    const { contentType, creatorLevel, industry } = router.query
    if (contentType || creatorLevel || industry) {
      setFilters({
        contentType: contentType?.toString() ?? '',
        creatorLevel: creatorLevel?.toString() ?? '',
        industry: industry?.toString() ?? ''
      })
    }
  }, [router.query])

  const {
    data: samplePosts,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['sample posts', wpContentCategorySlug, filters],
    ({ pageParam }) =>
      fetchSamplePosts({
        categories: [
          wpContentCategorySlug,
          filters.contentType,
          filters.industry,
          filters.creatorLevel
        ],
        after: pageParam,
        categorySlug: filters.contentType
      }),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      cacheTime: Infinity,
      initialData: () => ({
        pageParams: [undefined, 1],
        pages: [postsData]
      }),
      getNextPageParam: lastPage =>
        lastPage.posts.pageInfo.hasNextPage
          ? lastPage.posts.pageInfo.endCursor
          : false
    }
  )

  const contentCategoryMetadata = getContentCategoryMetadata(
    wpContentCategorySlug
  )

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleResetFilter = () => setFilters({ ...initialFilterState })

  const isDesignCategory = contentCategoryMetadata.slug.includes('design')

  const postGroup = samplePosts?.pages.map(data => data.posts.nodes) ?? []
  const posts = ([] as SamplePost[]).concat(...postGroup)
  const categoryDetails = samplePosts?.pages[0].categoryDetails

  const breadcrumbs = [
    { name: 'Samples', url: '/samples' },
    {
      name: getContentCategoryMetadata(wpContentCategorySlug).title,
      url: getContentCategoryMetadata(wpContentCategorySlug).slug
    }
  ]

  const renderFilterTags = () => {
    const contentTypeFilter =
      filters.contentType &&
      contentTypes.find(item => item.slug === filters.contentType)
    const industriesFilter =
      filters.industry &&
      industries.find(item => item.slug === filters.industry)
    const creatorLevelFilter =
      filters.creatorLevel &&
      creatorLevels.find(item => item.slug === filters.creatorLevel)

    if (contentTypeFilter || industriesFilter || creatorLevelFilter) {
      return (
        <div className="flex flex-wrap gap-4  |  mb-6">
          {contentTypeFilter && (
            <FilterChipButton
              onClick={() => handleFilterChange('contentType', '')}
              title={contentTypeFilter.name}
            />
          )}
          {industriesFilter && (
            <FilterChipButton
              onClick={() => handleFilterChange('industry', '')}
              title={industriesFilter.name}
            />
          )}
          {creatorLevelFilter && (
            <FilterChipButton
              onClick={() => handleFilterChange('creatorLevel', '')}
              title={creatorLevelFilter.name}
            />
          )}
        </div>
      )
    }
  }

  const isPostsLoading = isFetching && !isFetchingNextPage

  const onCtaClick = () => {
    triggerServiceOrderingFlow({})
  }

  return (
    <Layout
      title={`${contentCategoryMetadata.title} | Pepper Content`}
      canonical={contentCategoryMetadata.slug.replace('/', '')}
      jsonSchema={[constructBreadcrumbJsonSchema(breadcrumbs)]}
      noIndex={true}
    >
      <header className="bg-yellow-10 py-5 lg:py-8">
        <div className="max-desktop-width mx-auto px-5 sm:px-10 lg:px-6   |  flex justify-between items-center">
          <h1 className="h4 font-bold text-purple-70">
            Samples {categoryDetails && <span>for {categoryDetails.name}</span>}
          </h1>
          {categoryDetails === null && (
            <p className="hidden lg:block  |  text-paragraph-4 text-gray-70">
              A collection of content writing and design sample projects by
              Pepper.
            </p>
          )}
        </div>
      </header>

      <nav
        className="bg-white"
        style={{ filter: 'drop-shadow(0px 2px 4px rgba(70, 77, 83, 0.08))' }}
      >
        <div className="max-desktop-width mx-auto px-5 sm:px-10 lg:px-6  |  flex items-center justify-between">
          <ul className="flex overflow-x-auto  |  text-paragraph-3 text-gray-60 font-medium  |  space-x-6 lg:space-x-8">
            {categoryList.map(category => (
              <li key={category.title}>
                <Link
                  href={category.slug}
                  className={clsx(
                    'block |  whitespace-nowrap  |  py-4 lg:py-5  |  focus:outline-none focus:text-blue-50',
                    router.asPath.includes(category.slug) &&
                      'text-blue-50 border-b-2 border-blue-40'
                  )}
                >
                  {category.title}
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden xl:block">
            <SamplesFilterDropdown
              filters={filters}
              industries={industries}
              contentTypes={contentTypes}
              creatorLevels={creatorLevels}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>
      </nav>

      <div className="xl:hidden  |  px-5 py-6">
        <SamplesFilterDropdown
          filters={filters}
          industries={industries}
          contentTypes={contentTypes}
          creatorLevels={creatorLevels}
          handleFilterChange={handleFilterChange}
        />
      </div>

      <section className="max-desktop-width mx-auto mt-8 px-5 sm:px-10 lg:px-6  |  grid gap-8 xl:grid-cols-12">
        {/* content type filter on desktop screens */}
        <div className="hidden xl:block  |  xl:col-span-3  |  mb-16">
          <ul>
            {contentTypes.map(item => (
              <li key={item.id}>
                <button
                  className={clsx(
                    'flex items-center  |  text-gray-60 text-left  |  rounded-lg  |  min-h-8 w-full  |  px-3 py-2  |  focus:outline-none focus:text-blue-50 focus:bg-blue-10  |  transition-colors ease-in',
                    item.slug === filters.contentType &&
                      'text-blue-50 bg-blue-10'
                  )}
                  style={{ maxWidth: 250, fontSize: 13 }}
                  onClick={() => handleFilterChange('contentType', item.slug)}
                >
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* samples listing */}
        <div className="xl:col-span-9  |  mb-16  |  overflow-hidden">
          {renderFilterTags()}

          {isPostsLoading ? (
            <div className="flex items-center justify-center  |  py-10 lg:py-24">
              <LoaderIcon className="animate-spin  |  text-blue-50" />
            </div>
          ) : (
            <Fragment>
              <SamplesListing
                samples={posts}
                type={isDesignCategory ? 'design' : 'text'}
              />
              {hasNextPage && (
                <div className="text-center  |  mt-10 mx-auto">
                  <Button
                    onClick={() => fetchNextPage()}
                    size="small"
                    type="secondary"
                    loading={isFetchingNextPage}
                  >
                    <span className="font-normal">View More •••</span>
                  </Button>
                </div>
              )}
            </Fragment>
          )}

          {posts.length === 0 && !isPostsLoading && (
            <div className="text-center  |  py-10 lg:py-24">
              <img
                src="/assets/icons/folder-search.svg"
                alt="Folder Search"
                className="mx-auto mb-6"
              />
              <p className="text-paragraph-4 text-gray-60  |  mb-1">
                Sorry. We couldn’t find any samples for this requirement.
              </p>

              <button
                className="text-paragraph-4 font-medium underline"
                onClick={handleResetFilter}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      <GettingStarted
        title="Get a custom estimate"
        list={[
          { id: '1', Item: 'Quality guaranteed' },
          { id: '2', Item: 'Scales with you' },
          { id: '3', Item: 'Easy to get started' }
        ]}
        ctaTitle="Get Started"
        sectionName="Sample_Category_Listing_Page"
        onCtaClick={onCtaClick}
      />
    </Layout>
  )
}

const fetchSamplePosts = async ({
  categories = [],
  after,
  limit = 18,
  categorySlug = ''
}: {
  categories?: string[]
  after?: string
  limit?: number
  categorySlug?: string
}) => {
  const postsResult = await getWordpressGraphqlData<SamplePostListingResponse>(
    sampleListQuery({ categories, limit, after, categorySlug }).loc?.source.body
  )
  return postsResult
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const categorySlug = params?.categorySlug?.toString()

  let wpContentTypeSlug: WpContentTypeSlug
  let wpContentCategorySlug: WpContentCategorySlug
  switch (categorySlug) {
    case 'content-writing':
      wpContentTypeSlug = 'content-type-text'
      wpContentCategorySlug = 'content-category-text'
      break
    case 'design':
      wpContentTypeSlug = 'content-type-design'
      wpContentCategorySlug = 'content-category-design'
      break
    case 'language':
      wpContentTypeSlug = 'content-type-language'
      wpContentCategorySlug = 'content-category-language'
      break
    default:
      return { notFound: true }
  }

  const metadataResult =
    await getWordpressGraphqlData<ContentCategorySamplePageMetadataResponse>(
      contentCategoryPageQuery(wpContentTypeSlug).loc?.source.body
    )
  const postsResult = await fetchSamplePosts({
    categories: [wpContentCategorySlug]
  })

  const contentTypes = [
    { id: '1', slug: '', name: 'All in Content' },
    ...metadataResult.contentTypes.nodes[0].children.nodes
  ]
  const industries = metadataResult.industries.nodes[0].children.nodes
  const creatorLevels = metadataResult.creatorLevel.nodes[0].children.nodes

  return {
    props: {
      postsData: postsResult,
      initialFilterState,
      contentTypes,
      industries,
      creatorLevels,
      wpContentCategorySlug,
      categorySlug
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}
